import {
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  PaginatedResponse,
  useLoading,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LabelLarge } from 'baseui/typography';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CONVOY_SEPARATION } from '../../constants/liveStatus';
import useEffectOnce from '../../hooks/useEffectOnce';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { downloadLiveStatusDetail, getLiveStatusDetail } from '../../services/liveStatusService';
import { apiParamsState } from '../../storage/app';
import { liveStatusInViewState, liveStatusSelectState } from '../../storage/liveStatus';
import { LiveStatusConvoyDetail, LiveStatusDetail } from '../../types/liveStatus';
import { StyledTooltip } from '../ui/StyledTooltip';
import LiveStatusDetailCard from './LiveStatusDetailCard';

export function LiveStatusDetailList() {
  const { css, theme } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const { identifier } = useParams();
  const liveStatus = useRecoilValue(liveStatusSelectState(identifier ?? ''));
  const { loading, startLoading, stopLoading } = useLoading();
  const { showFailFeedback } = useFeedback();
  const setPointsInView = useSetRecoilState(liveStatusInViewState);
  const [page, setPage] = useState(1);
  const [paginatedLiveStatusDetail, setPaginatedLiveStatusDetail] =
    useState<PaginatedResponse<LiveStatusConvoyDetail | LiveStatusDetail>>();

  const load = async () => {
    if (!apiParams?.clientId || !liveStatus?.detailUrl || !apiParams?.subClientId) return;
    startLoading();
    const { paginatedResponse, error } = await getLiveStatusDetail(
      apiParams.clientId,
      apiParams.subClientId,
      page,
      liveStatus.detailUrl,
      false
    );
    if (!error && paginatedResponse) {
      if (page > 1) {
        setPaginatedLiveStatusDetail((prev) => ({
          hasNext: paginatedResponse.hasNext,
          total: paginatedResponse.total,
          items: [...prev!.items, ...paginatedResponse.items],
        }));
      } else {
        setPaginatedLiveStatusDetail(paginatedResponse);
      }
    } else {
      showFailFeedback('Error al obtener la información');
      setPaginatedLiveStatusDetail(undefined);
    }
    stopLoading();
  };

  useEffectOnce(() => {
    load();
    return () => {
      setPage(1);
    };
  });

  useUpdateEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }

    load();
  }, [page]);

  useEffect(() => {
    if (!paginatedLiveStatusDetail || identifier === CONVOY_SEPARATION) {
      setPointsInView(undefined);
    }
    setPointsInView(paginatedLiveStatusDetail?.items as LiveStatusDetail[]);
  }, [paginatedLiveStatusDetail]);

  const downloadFile = async () => {
    if (!apiParams?.clientId || !liveStatus?.detailUrl || !apiParams?.subClientId) return;
    const response = await downloadLiveStatusDetail(apiParams.clientId, apiParams.subClientId, liveStatus.detailUrl);
    if (response.csv && !response.error) {
      const element = document.createElement('a');
      element.setAttribute('href', 'data:application/octet-stream,' + encodeURIComponent(response.csv));
      element.setAttribute('download', `${liveStatus.label}.csv`);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } else {
      showFailFeedback('Error al generar el archivo');
    }
  };

  return (
    <FlexColumn
      gap={0}
      classNames={css({ height: 'calc(100vh - 116px)' })}
    >
      {!paginatedLiveStatusDetail && !loading && <EmptyState title="No cuanta con ningun elemento" />}
      {!paginatedLiveStatusDetail && loading && <Loading />}
      {paginatedLiveStatusDetail && (
        <FlexColumn
          gap={theme.sizing.scale300}
          classNames={css({ height: '100%' })}
        >
          <FlexRow classNames={css({ justifyContent: 'space-between' })}>
            <LabelLarge>{liveStatus?.label}</LabelLarge>
            <StyledTooltip
              content={'Descargar'}
              showArrow={false}
            >
              <Button
                size="compact"
                shape="square"
                onClick={downloadFile}
                kind="tertiary"
              >
                <MaterialIcon name={'download'} />
              </Button>
            </StyledTooltip>
          </FlexRow>
          <div
            id="scrollableDiv"
            className={css({ height: '100%', overflow: 'auto' })}
          >
            <InfiniteScroll
              dataLength={paginatedLiveStatusDetail?.items.length ?? 0} //This is important field to render the next data
              next={() => setPage(page + 1)}
              hasMore={paginatedLiveStatusDetail?.hasNext ?? false}
              loader={<></>}
              endMessage={
                paginatedLiveStatusDetail?.hasNext && (
                  <p style={{ textAlign: 'center' }}>
                    <b>Cargando...</b>
                  </p>
                )
              }
              scrollableTarget="scrollableDiv"
            >
              <FlexColumn gap={'16px'}>
                {paginatedLiveStatusDetail.items.map((status, i) => (
                  <LiveStatusDetailCard
                    key={i}
                    identifier={identifier ?? ''}
                    data={status}
                  />
                ))}
              </FlexColumn>
            </InfiniteScroll>
            {!paginatedLiveStatusDetail.items.length && <EmptyState title="No se encontraron resultados" />}
          </div>
        </FlexColumn>
      )}
    </FlexColumn>
  );
}
